import { createPublicClient, createWalletClient, custom, decodeEventLog, getAddress, http, isAddress, parseAbi, zeroAddress, type Address, type EIP1193Provider, type Hash } from 'viem';
import { bsc, bscTestnet } from 'viem/chains';

/*
 * rules/rule15.txt section 4 - "Convert to CDTT" (Community Discount Token), one-click from the
 * member's token page. Two on-chain steps, both signed by the member's wallet:
 *   1. coordinator.requestRedemption(CDTT provider id, tokenId) - eligibility and the 365-day
 *      freeze are enforced by the contracts; the Primary Token goes into escrow.
 *   2. provider.convert(redemptionId) - mints CDTT (parity x multiplier) and finalizes the
 *      Primary Token as Redeemed Externally. One final redemption, never reversed.
 */

const COORDINATOR_ABI = parseAbi([
  'function requestRedemption(bytes32 providerId, uint256 tokenId) returns (uint256)',
  'event RedemptionRequested(uint256 indexed redemptionId, uint256 indexed tokenId, bytes32 indexed providerId, uint64 providerVersion, address requester, uint256 settlementAmount, uint64 expiresAt)',
]);
const PROVIDER_ABI = parseAbi([
  'function convert(uint256 redemptionId)',
  'function PROVIDER_ID() view returns (bytes32)',
]);

export interface CdttConfiguration {
  chainId: 56 | 97;
  rpcUrl: string;
  coordinator: Address;
  provider: Address;
  token: Address;
  liquidation: Address | null;
}

function configuredAddress(value: string | undefined): Address | null {
  if (!value || !isAddress(value)) return null;
  const address = getAddress(value);
  return address === zeroAddress ? null : address;
}

export function resolveCdttConfiguration(env: ImportMetaEnv = import.meta.env): CdttConfiguration | null {
  const chainId = Number(env.VITE_CHAIN_ID ?? 97);
  if (chainId !== 56 && chainId !== 97) return null;
  const coordinator = configuredAddress(env.VITE_REDEMPTION_COORDINATOR_PROXY_ADDRESS);
  const provider = configuredAddress(env.VITE_CDTT_PROVIDER_ADDRESS);
  const token = configuredAddress(env.VITE_CDTT_TOKEN_ADDRESS);
  if (!coordinator || !provider || !token) return null;
  const rpcUrl = env.VITE_RPC_PUBLIC_URL?.trim() || (chainId === 56 ? 'https://bsc-dataseed.bnbchain.org' : 'https://data-seed-prebsc-1-s1.bnbchain.org:8545');
  return { chainId, rpcUrl, coordinator, provider, token, liquidation: configuredAddress(env.VITE_LIQUIDATION_PROXY_ADDRESS) };
}

export type CdttStep = 'requesting' | 'escrowed' | 'converting' | 'done';

const LIQUIDATION_ABI = parseAbi([
  'function liquidate(uint256 redemptionId)',
  'function PROVIDER_ID() view returns (bytes32)',
]);

/** Rule 21 s.4 / 23 s.4: native liquidation - request under the NATIVE_LIQUIDATION provider, then liquidate. */
export async function liquidatePrimaryToken(
  config: CdttConfiguration,
  account: Address,
  tokenId: bigint,
  onProgress: (step: CdttStep, hash?: Hash) => void,
): Promise<CdttConversionResult> {
  if (!window.ethereum) throw new Error('No injected EVM wallet is available.');
  if (!config.liquidation) throw new Error('Liquidation contract is not configured.');
  const chain = config.chainId === 56 ? bsc : bscTestnet;
  const publicClient = createPublicClient({ chain, transport: http(config.rpcUrl, { timeout: 10_000, retryCount: 2 }) });
  const walletClient = createWalletClient({ account, chain, transport: custom(window.ethereum as unknown as EIP1193Provider) });
  const providerId = await publicClient.readContract({ address: config.liquidation, abi: LIQUIDATION_ABI, functionName: 'PROVIDER_ID' });
  onProgress('requesting');
  const requestHash = await walletClient.writeContract({ address: config.coordinator, abi: COORDINATOR_ABI, functionName: 'requestRedemption', args: [providerId, tokenId] });
  const requestReceipt = await publicClient.waitForTransactionReceipt({ hash: requestHash });
  if (requestReceipt.status !== 'success') throw new Error('The redemption request was reverted on-chain.');
  let redemptionId: bigint | null = null;
  for (const log of requestReceipt.logs) {
    try { const decoded = decodeEventLog({ abi: COORDINATOR_ABI, data: log.data, topics: log.topics }); if (decoded.eventName === 'RedemptionRequested') { redemptionId = decoded.args.redemptionId; break; } } catch { /* not ours */ }
  }
  if (redemptionId === null) throw new Error('The redemption id could not be read from the receipt.');
  onProgress('escrowed', requestHash);
  onProgress('converting');
  const convertHash = await walletClient.writeContract({ address: config.liquidation, abi: LIQUIDATION_ABI, functionName: 'liquidate', args: [redemptionId] });
  const receipt = await publicClient.waitForTransactionReceipt({ hash: convertHash });
  if (receipt.status !== 'success') throw new Error('The liquidation was reverted on-chain.');
  onProgress('done', convertHash);
  return { redemptionId, requestHash, convertHash };
}

export interface CdttConversionResult {
  redemptionId: bigint;
  requestHash: Hash;
  convertHash: Hash;
}

export async function convertPrimaryTokenToCdtt(
  config: CdttConfiguration,
  account: Address,
  tokenId: bigint,
  onProgress: (step: CdttStep, hash?: Hash) => void,
): Promise<CdttConversionResult> {
  if (!window.ethereum) throw new Error('No injected EVM wallet is available.');
  const chain = config.chainId === 56 ? bsc : bscTestnet;
  const publicClient = createPublicClient({ chain, transport: http(config.rpcUrl, { timeout: 10_000, retryCount: 2 }) });
  const walletClient = createWalletClient({ account, chain, transport: custom(window.ethereum as unknown as EIP1193Provider) });

  const providerId = await publicClient.readContract({ address: config.provider, abi: PROVIDER_ABI, functionName: 'PROVIDER_ID' });

  onProgress('requesting');
  const requestHash = await walletClient.writeContract({
    address: config.coordinator,
    abi: COORDINATOR_ABI,
    functionName: 'requestRedemption',
    args: [providerId, tokenId],
  });
  const requestReceipt = await publicClient.waitForTransactionReceipt({ hash: requestHash });
  if (requestReceipt.status !== 'success') throw new Error('The redemption request was reverted on-chain.');
  let redemptionId: bigint | null = null;
  for (const log of requestReceipt.logs) {
    try {
      const decoded = decodeEventLog({ abi: COORDINATOR_ABI, data: log.data, topics: log.topics });
      if (decoded.eventName === 'RedemptionRequested') { redemptionId = decoded.args.redemptionId; break; }
    } catch { /* not ours */ }
  }
  if (redemptionId === null) throw new Error('The redemption id could not be read from the receipt.');
  onProgress('escrowed', requestHash);

  onProgress('converting');
  const convertHash = await walletClient.writeContract({
    address: config.provider,
    abi: PROVIDER_ABI,
    functionName: 'convert',
    args: [redemptionId],
  });
  const convertReceipt = await publicClient.waitForTransactionReceipt({ hash: convertHash });
  if (convertReceipt.status !== 'success') throw new Error('The CDTT conversion was reverted on-chain.');
  onProgress('done', convertHash);
  return { redemptionId, requestHash, convertHash };
}
